import React, { useState, useEffect } from 'react';
import { ArrowLeft, Settings, Users, FolderOpen, Calendar, MoreVertical } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { ProjectService } from '../../services/projectService';
import { GoogleDirectoryService, GoogleDirectoryUser } from '../../services/googleDirectory';
import { TaskBoard } from '../Tasks';
import { ProjectTeamModal } from './ProjectTeamModal';
import { ProjectSettingsModal } from './ProjectSettingsModal';
import { Project } from '../../types';

interface ProjectDetailViewProps {
  project: Project;
  onBack: () => void;
  onProjectUpdate?: (updatedProject: Project) => void;
  onProjectDelete?: (projectId: string) => void;
  onTasksChanged?: () => void;
}

export const ProjectDetailView: React.FC<ProjectDetailViewProps> = ({
  project,
  onBack,
  onProjectUpdate,
  onProjectDelete,
  onTasksChanged
}) => {
  const { firebaseUser } = useAuth();
  const [projectMembers, setProjectMembers] = useState<GoogleDirectoryUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showMenu, setShowMenu] = useState(false);
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

  const projectService = ProjectService.getInstance();
  const directoryService = GoogleDirectoryService.getInstance();

  useEffect(() => {
    loadProjectMembers();
  }, [project.id]);

  // Load project members (via Firebase Functions)
  const loadProjectMembers = async () => {
    if (!firebaseUser) return;

    try {
      setLoading(true);
      setError('');
      
      console.log('👥 Loading project members...');
      
      // Get project members from Firestore
      const members = await projectService.getProjectMembers(project.id);
      
      // Get user details from Google Directory (calls Firebase Functions with service account)
      const memberDetails: GoogleDirectoryUser[] = [];
      
      // Add project owner
      const orgUsers = await directoryService.getOrganizationUsers(firebaseUser);
      const ownerUser = orgUsers.find(user => user.id === project.ownerId);
      if (ownerUser) {
        memberDetails.push(ownerUser);
      }
      
      // Add project members
      for (const member of members) {
        const memberUser = orgUsers.find(user => user.id === member.userId);
        if (memberUser && !memberDetails.find(u => u.id === memberUser.id)) {
          memberDetails.push(memberUser);
        }
      }
      
      console.log('✅ Loaded project members:', memberDetails.length);
      setProjectMembers(memberDetails);
    } catch (err: any) {
      console.error('❌ Error loading project members:', err);
      setError(`Failed to load project members: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDriveFolder = () => {
    if (project.driveFolder?.url) {
      window.open(project.driveFolder.url, '_blank');
    }
  };

  const getStatusColor = (status: Project['status']) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'archived': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const isProjectOwner = firebaseUser?.uid === project.ownerId;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onBack}
              className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-1" />
              Back to Projects
            </button>
            <div className="h-6 w-px bg-gray-300" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
              <p className="text-gray-600 mt-1">{project.description || 'No description provided'}</p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {/* Project Status */}
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(project.status)}`}>
              {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
            </span>

            {/* Drive Folder Link */}
            {project.driveFolder && (
              <button
                onClick={handleOpenDriveFolder}
                className="flex items-center px-3 py-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                title="Open Google Drive folder"
              >
                <FolderOpen className="w-5 h-5 mr-2" />
                Drive Folder
              </button>
            )}

            {/* Project Menu */}
            {isProjectOwner && (
              <div className="relative">
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <MoreVertical className="w-5 h-5" />
                </button>
                
                {showMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                  <div className="py-1">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsSettingsModalOpen(true);
                        setShowMenu(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Project Settings
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsTeamModalOpen(true);
                        setShowMenu(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Manage Members
                    </button>
                  </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Project Meta */}
        <div className="flex items-center space-x-6 mt-4 text-sm text-gray-500">
          <div className="flex items-center">
            <Calendar className="w-4 h-4 mr-1" />
            Created {new Date(project.createdAt).toLocaleDateString()}
          </div>
          <div className="flex items-center">
            <Calendar className="w-4 h-4 mr-1" />
            Updated {new Date(project.updatedAt).toLocaleDateString()}
          </div>
          <div className="flex items-center">
            <Users className="w-4 h-4 mr-1" />
            {projectMembers.length} member{projectMembers.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="mx-6 mt-4 flex items-center justify-center h-16">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading project data...</span>
        </div>
      )}

      {/* Main Content - Task Board */}
      <div className="flex-1 p-6 overflow-hidden">
        <TaskBoard 
          projectId={project.id} 
          projectMembers={projectMembers}
          onTasksChanged={onTasksChanged}
        />
      </div>

      {/* Project Team Modal */}
      <ProjectTeamModal
        isOpen={isTeamModalOpen}
        onClose={() => setIsTeamModalOpen(false)}
        project={project}
        onTeamUpdated={loadProjectMembers}
      />

      {/* Project Settings Modal */}
      <ProjectSettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
        project={project}
        onProjectUpdated={(updatedProject) => {
          if (onProjectUpdate) {
            onProjectUpdate(updatedProject);
          }
        }}
        onProjectDeleted={(projectId) => {
          if (onProjectDelete) {
            onProjectDelete(projectId);
          } else {
            onBack(); // Fallback - just go back to projects list
          }
        }}
      />

      {/* Click outside to close menu */}
      {showMenu && (
        <div 
          className="fixed inset-0 z-0" 
          onClick={() => setShowMenu(false)}
        />
      )}
    </div>
  );
};
